/**
 * GlobalTools Messaging Abstraction Layer
 *
 * Provides a clean, unified API for tools to interact with the GlobalChatSystem.
 * Wraps the underlying streaming and message helpers with a more ergonomic interface.
 */

import { getPendingWidgetHtml } from './widgetHtml';
import {
  generateSpecialMessageTemplate,
  formatResearchToHtml,
  formatDocumentToHtml,
  isDocumentContent,
  SPECIAL_MESSAGE_THEMES,
  PREST0_PRIMARY_CTA_BG,
} from '../agenticWorkflows/helpers';
import type { SpecialMessageColorPalette } from '../agenticWorkflows/helpers';
import { renderStructuredEvent } from './renderStructuredEvent';
import { isToolStructuredEvent } from './structuredEvents';
import { STRUCTURED_EVENT_RENDERING } from './featureFlags';

import type {
  MessagingHelpers,
  StreamingOptions,
  StreamingHandle,
  StreamingUpdate,
  StreamingFinal,
  SpecialMessageConfig,
  ButtonConfig,
  WorkflowHelpersInterface,
  ToolTheme,
  SharedWorkflowContext,
  TokenUsageMetadata,
} from './types';
import type { SystemMessageInput, ChatMessage } from '../../components/GlobalChatSystem/types';
import type { DocumentAnalysisResult } from '../claudeDocumentAnalyzer';
import { CLAUDE_DOCUMENT_ANALYSIS_DISPLAY } from '../../config/agenticWorkflowConfig';
import { buildModelDisplay } from './modelDisplay';

// ============================================================================
// Error & Success Icons (inline SVG for consistency)
// ============================================================================

const ERROR_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:16px;height:16px;"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>`;

const SUCCESS_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:16px;height:16px;"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>`;

const WARNING_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:16px;height:16px;"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/></svg>`;

const INFO_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width:16px;height:16px;"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>`;

// ============================================================================
// Shared finalize helper
// ============================================================================

/**
 * Dedupe key for a usage entry. 2026-08-16 (Miles): keying on
 * (summary, model) alone collapsed two genuinely distinct calls to the same
 * model with the same summary — the key now includes the token counts and
 * cost so only TRUE duplicates (the same call arriving via two paths) merge.
 * Order stays stable: the filter keeps each key's first occurrence in place.
 */
const usageDedupeKey = (u: TokenUsageMetadata): string =>
  `${u.summary ?? ''}|${u.model ?? ''}|${u.inputTokens ?? ''}|${u.outputTokens ?? ''}|${u.upstreamCostUsd ?? ''}`;

/**
 * Finalize a streaming/pending message produced by a tool. Centralised so
 * the payload-rendering branch is shared by both startStreaming() and
 * showPending().
 *
 * 2026-05-10 (Miles directive): when `final.payload` is present and
 * STRUCTURED_EVENT_RENDERING is on, we kick off the renderer LLM and
 * stream chunks into the message. We do NOT call finalizeStreamingMessage
 * yet — the renderer's resolution does that. While the renderer runs,
 * the message UI shows a small "composing reply" affordance instead of
 * the tool's hardcoded text.
 *
 * When `final.payload` is absent, we preserve the legacy behaviour of
 * writing `final.text` / `final.html` directly. The legacy code path
 * remains because some finalize calls are status-tile-only and don't
 * need LLM rendering (e.g. a green "5 attachments read" badge).
 */
function finalizeStreamingFromTool(args: {
  helpers: WorkflowHelpersInterface;
  sharedContext?: SharedWorkflowContext;
  id: string;
  index: number;
  final: StreamingFinal;
  isPendingStream: boolean;
}): void {
  const { helpers, sharedContext, id, index, final } = args;

  // Consume pending token usages and merge with final's usages
  const pendingUsages = sharedContext?.consumePendingTokenUsages?.() || [];
  const combinedUsages: TokenUsageMetadata[] = [
    ...pendingUsages,
    ...(final.tokenUsages || []),
    ...(final.tokenUsage ? [final.tokenUsage] : []),
  ];
  const uniqueUsages = combinedUsages.filter((usage, idx, self) =>
    idx === self.findIndex((u) => usageDedupeKey(u) === usageDedupeKey(usage))
  );

  // ── Payload-rendered branch ─────────────────────────────────────────────
  if (final.payload && STRUCTURED_EVENT_RENDERING && isToolStructuredEvent(final.payload)) {
    const event = final.payload;
    // Show "composing reply" affordance immediately so the spinner doesn't
    // freeze on the previous tool widget while the renderer runs.
    if (helpers.streaming.updateStreamingMessageById) {
      helpers.streaming.updateStreamingMessageById(id, {
        text: event.summary || '',
        html: getPendingWidgetHtml('composing reply'),
      });
    } else {
      helpers.streaming.updateStreamingMessage(index, {
        text: event.summary || '',
        html: getPendingWidgetHtml('composing reply'),
      });
    }

    let accumulated = '';
    renderStructuredEvent({
      event,
      onChunk: (_chunk: string, acc: string) => {
        accumulated = acc;
        // GHOST-BUBBLE FIX (owner screenshot 2026-08-19): an empty/whitespace
        // first delta used to replace the "composing reply" widget with
        // {text:'', html:''} — a blank assistant bubble that renders inline,
        // persists across reload, and (if the renderer then stalls) leaves
        // the pill stuck with nothing pending-shaped behind it. Keep the
        // widget up until real content arrives.
        if (!acc.trim()) return;
        if (helpers.streaming.updateStreamingMessageById) {
          helpers.streaming.updateStreamingMessageById(id, { text: acc, html: '' });
        } else {
          helpers.streaming.updateStreamingMessage(index, { text: acc, html: '' });
        }
      },
    })
      .then((result) => {
        const renderedText = result.text || accumulated || event.summary || '';
        const finalUsages: TokenUsageMetadata[] =
          result.tokenUsage ? [...uniqueUsages, result.tokenUsage] : uniqueUsages;
        const dedupedUsages = finalUsages.filter((u, idx, self) =>
          idx === self.findIndex((x) => usageDedupeKey(x) === usageDedupeKey(u))
        );
        const payloadOut: Partial<ChatMessage> = {
          text: renderedText,
          html: '',
          sourceTag: final.sourceTag,
          attachments: final.attachments || event.attachments,
          attachmentsSerialized: final.attachmentsSerialized,
          tokenUsages: dedupedUsages.length > 0 ? dedupedUsages : undefined,
          tokenUsage: dedupedUsages.length === 1 ? dedupedUsages[0] : final.tokenUsage,
          title: final.title,
          workflowOutput: final.workflowOutput,
          workflowActionButtonsHtml: final.workflowActionButtonsHtml,
        };
        if (helpers.streaming.finalizeStreamingMessageById) {
          helpers.streaming.finalizeStreamingMessageById(id, payloadOut);
        } else {
          helpers.streaming.finalizeStreamingMessage(index, payloadOut);
        }
      })
      .catch((err) => {
        console.error('[messaging] finalize: renderStructuredEvent failed:', err);
        const fallback = `(Renderer unavailable for ${event.kind}; please retry.)`;
        const payloadOut: Partial<ChatMessage> = {
          text: fallback,
          html: '',
          sourceTag: final.sourceTag,
        };
        if (helpers.streaming.finalizeStreamingMessageById) {
          helpers.streaming.finalizeStreamingMessageById(id, payloadOut);
        } else {
          helpers.streaming.finalizeStreamingMessage(index, payloadOut);
        }
      });

    return;
  }

  // ── Legacy text/html branch ─────────────────────────────────────────────
  const legacyPayload = {
    text: final.text,
    html: final.html,
    sourceTag: final.sourceTag,
    attachments: final.attachments,
    attachmentsSerialized: final.attachmentsSerialized,
    tokenUsages: uniqueUsages.length > 0 ? uniqueUsages : undefined,
    tokenUsage: uniqueUsages.length === 1 ? uniqueUsages[0] : final.tokenUsage,
    title: final.title,
    workflowOutput: final.workflowOutput,
    workflowActionButtonsHtml: final.workflowActionButtonsHtml,
  };
  if (helpers.streaming.finalizeStreamingMessageById) {
    helpers.streaming.finalizeStreamingMessageById(id, legacyPayload);
  } else {
    helpers.streaming.finalizeStreamingMessage(index, legacyPayload);
  }
}

// ============================================================================
// Theme Resolution
// ============================================================================

/**
 * Resolve a theme name to its color palette.
 */
function resolveThemeColors(
  theme: 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'custom',
  customColors?: ToolTheme
): SpecialMessageColorPalette {
  if (theme === 'custom' && customColors) {
    return customColors;
  }
  return SPECIAL_MESSAGE_THEMES[theme === 'custom' ? 'blue' : theme];
}

// ============================================================================
// Messaging Helpers Factory
// ============================================================================

/**
 * Create MessagingHelpers from the underlying WorkflowHelpersInterface.
 * This adapts the existing chat helpers to the new abstraction layer.
 *
 * @param helpers - The underlying workflow helpers interface
 * @param sharedContext - Optional shared context for auto-consuming pending token usages
 */
export function createMessagingHelpers(
  helpers: WorkflowHelpersInterface,
  sharedContext?: SharedWorkflowContext
): MessagingHelpers {
  return {
    /**
     * Add a system message (non-streaming).
     *
     * 2026-05-10 (Miles directive): when `input.payload` is present and
     * `STRUCTURED_EVENT_RENDERING` is on, we add a placeholder message
     * immediately, then asynchronously call the renderer LLM and update
     * the placeholder with the rendered text. The placeholder uses the
     * tool's theme/sourceTag and shows a small "thinking" affordance via
     * the existing pending-widget HTML so the user sees something instead
     * of a blank line while the renderer runs.
     */
    addSystemMessage: (input: SystemMessageInput): ChatMessage => {
      // renderPolicy:'never' (2026-08-20): the payload posts DATA-ONLY —
      // no renderer model call, no composed prose. Used by the connect
      // cards, whose accompanying sentence is authored by the ORCHESTRATOR
      // in its own turn (owner directive: special messages are composed
      // under the primary orchestrator's authority, never a side model).
      // The base sink retains payload + buttons on the message.
      if (input.payload && input.renderPolicy !== 'never' && STRUCTURED_EVENT_RENDERING && isToolStructuredEvent(input.payload)) {
        // Insert a placeholder streaming message and kick off the render
        // asynchronously. The placeholder message is the value we return —
        // by the time React paints, the renderer will have started filling
        // it in.
        const event = input.payload;
        const { index, id } = helpers.streaming.startStreamingMessage({
          text: event.summary || '',
          html: getPendingWidgetHtml('composing reply'),
          sourceTag: input.sourceTag || `payload_${event.kind}`,
        });

        let accumulated = '';
        // BOUNDED RENDER (owner repro 2026-08-20: a hung renderer left the
        // "composing reply" placeholder spinning forever — the .catch path
        // never fires on a hang). One finalize wins; the timeout finalizes
        // deterministically from the event's own summary, buttons intact.
        let renderSettled = false;
        const finalizeOnce = (finalPayload: Partial<ChatMessage>) => {
          if (renderSettled) return;
          renderSettled = true;
          if (helpers.streaming.finalizeStreamingMessageById) {
            helpers.streaming.finalizeStreamingMessageById(id, finalPayload);
          } else {
            helpers.streaming.finalizeStreamingMessage(index, finalPayload);
          }
        };
        const renderAbort = new AbortController();
        const RENDER_TIMEOUT_MS = 25_000;
        const renderTimer = setTimeout(() => {
          renderAbort.abort();
          console.warn('[messaging] structured render timed out — deterministic fallback', { kind: event.kind });
          finalizeOnce({
            text: accumulated || event.summary || `(${event.kind})`,
            html: '',
            sourceTag: input.sourceTag || `payload_${event.kind}_fallback`,
            workflowActionButtonsHtml: input.workflowActionButtonsHtml,
          });
        }, RENDER_TIMEOUT_MS);
        renderStructuredEvent({
          event,
          signal: renderAbort.signal,
          onChunk: (_chunk: string, acc: string) => {
            if (renderSettled) return;
            accumulated = acc;
            if (helpers.streaming.updateStreamingMessageById) {
              helpers.streaming.updateStreamingMessageById(id, { text: acc, html: '' });
            } else {
              helpers.streaming.updateStreamingMessage(index, { text: acc, html: '' });
            }
          },
        })
          .then((result) => {
            clearTimeout(renderTimer);
            const finalText = result.text || accumulated || event.summary || '';
            finalizeOnce({
              text: finalText,
              html: result.html || '',
              sourceTag: input.sourceTag || `payload_${event.kind}`,
              attachments: input.attachments || event.attachments,
              attachmentsSerialized: input.attachmentsSerialized,
              tokenUsage: result.tokenUsage || input.tokenUsage,
              // 2026-08-12 (connect-card incident): the structured-render
              // branch dropped the caller's workflowActionButtonsHtml, so a
              // payload-emitting tool that attached action buttons (the
              // connect-app offer card's one-click connect buttons) rendered
              // as prose with no buttons. The streaming-finalize wrapper
              // above already preserves the field — mirror it here.
              workflowActionButtonsHtml: input.workflowActionButtonsHtml,
            });
          })
          .catch((err) => {
            clearTimeout(renderTimer);
            console.error('[messaging] addSystemMessage: renderStructuredEvent failed:', err);
            finalizeOnce({
              text: accumulated || event.summary || `(Renderer unavailable for ${event.kind}; please retry.)`,
              html: '',
              sourceTag: input.sourceTag || `payload_${event.kind}_fallback`,
              // 2026-08-12: keep the buttons even when the renderer LLM
              // fails — the connect card is self-contained HTML and the
              // button click handler needs no rendered prose.
              workflowActionButtonsHtml: input.workflowActionButtonsHtml,
            });
          });

        // Return a placeholder ChatMessage matching what the streaming
        // helpers would have returned. The actual content will be filled
        // in by the renderer above.
        return {
          id,
          text: event.summary || '',
          html: getPendingWidgetHtml('composing reply'),
          isUser: false,
          timestamp: new Date(),
          sourceTag: input.sourceTag || `payload_${event.kind}`,
        };
      }

      return helpers.addSystemMessage(input);
    },

    /**
     * Start a streaming message and return a handle for updates.
     */
    startStreaming: (options: StreamingOptions): StreamingHandle => {
      const { index, id } = helpers.streaming.startStreamingMessage({
        text: options.initialText || '',
        html: options.initialHtml || '',
        sourceTag: options.sourceTag,
        messageType: options.messageType,
      });

      return {
        index,
        id,
        update: (content: StreamingUpdate) => {
          // Prefer ID-based updates (immune to React dispatch race conditions)
          if (helpers.streaming.updateStreamingMessageById) {
            helpers.streaming.updateStreamingMessageById(id, {
              text: content.text,
              html: content.html,
            });
          } else {
            helpers.streaming.updateStreamingMessage(index, {
              text: content.text,
              html: content.html,
            });
          }
        },
        finalize: (final: StreamingFinal) => {
          finalizeStreamingFromTool({
            helpers,
            sharedContext,
            id,
            index,
            final,
            isPendingStream: false,
          });
        },
        abort: () => {
          const abortPayload = {
            text: 'Cancelled',
            html: '<div style="color:#6b7280;font-style:italic;">Cancelled</div>',
            sourceTag: 'cancelled',
          };

          // Prefer ID-based finalize for abort
          if (helpers.streaming.finalizeStreamingMessageById) {
            helpers.streaming.finalizeStreamingMessageById(id, abortPayload);
          } else {
            helpers.streaming.finalizeStreamingMessage(index, abortPayload);
          }
        },
      };
    },

    /**
     * Show a pending/loading widget with a label.
     * Returns a StreamingHandle for subsequent updates or finalization.
     * Automatically registers stream for cancellation (converts to "stopped" if user clicks stop).
     */
    showPending: (label: string, sourceTag?: string): StreamingHandle => {
      const pendingHtml = getPendingWidgetHtml(label);
      const { index, id } = helpers.streaming.startStreamingMessage({
        text: `${label}...`,
        html: pendingHtml,
        sourceTag: sourceTag || 'pending',
      });

      const handle: StreamingHandle = {
        index,
        id,
        update: (content: StreamingUpdate) => {
          // Prefer ID-based updates (immune to React dispatch race conditions)
          if (helpers.streaming.updateStreamingMessageById) {
            helpers.streaming.updateStreamingMessageById(id, {
              text: content.text,
              html: content.html,
            });
          } else {
            helpers.streaming.updateStreamingMessage(index, {
              text: content.text,
              html: content.html,
            });
          }
        },
        finalize: (final: StreamingFinal) => {
          // Clear active stream registration (we're finalizing normally, not cancelled)
          sharedContext?.setActiveStream?.(null);
          finalizeStreamingFromTool({
            helpers,
            sharedContext,
            id,
            index,
            final,
            isPendingStream: true,
          });
        },
        abort: () => {
          // Clear active stream registration on abort
          sharedContext?.setActiveStream?.(null);
          // Prefer ID-based remove
          if (helpers.streaming.removeMessageById) {
            helpers.streaming.removeMessageById(id);
          } else {
            helpers.streaming.removeMessage(index);
          }
        },
      };

      // Register this stream for cancellation
      // If user clicks stop, cancelRunningTool() will finalize this with "stopped" message
      sharedContext?.setActiveStream?.({
        finalize: (content) => {
          // Prefer ID-based finalize for cancellation
          if (helpers.streaming.finalizeStreamingMessageById) {
            helpers.streaming.finalizeStreamingMessageById(id, content);
          } else {
            helpers.streaming.finalizeStreamingMessage(index, content);
          }
        },
      });

      return handle;
    },

    /**
     * Show a special message with sections and buttons.
     */
    showSpecialMessage: (config: SpecialMessageConfig): void => {
      const colors = resolveThemeColors(config.theme, config.customColors);

      // Convert ButtonConfig[] to TemplateButtonConfig[]
      const templateButtons = config.buttons?.map((btn) => ({
        label: btn.label,
        id: btn.id,
        background: btn.background || colors.accent,
        textColor: '#ffffff',
        dataAttributes: btn.dataAttributes,
        variant: 'primary' as const,
      }));

      const html = generateSpecialMessageTemplate({
        header: config.header,
        sections: config.sections,
        buttons: templateButtons,
        theme: config.theme,
        customColors: config.customColors ? {
          accent: config.customColors.accent,
          accentBg: config.customColors.accentBg,
          border: config.customColors.border,
          subtitle: config.customColors.subtitle,
          muted: config.customColors.muted,
        } : undefined,
        helperText: config.helperText,
      });

      helpers.addSystemMessage({
        text: config.header.title,
        html,
        sourceTag: 'special_message',
      });
    },

    /**
     * Show an error message.
     *
     * 2026-05-10 (Miles directive): now emits a generic structured event
     * so the post-tool renderer composes the user-visible reply in the
     * user's language and persona voice. The previous red-box HTML tile
     * is no longer used.
     */
    showError: (title: string, message: string): void => {
      helpers.addSystemMessage({
        payload: {
          kind: 'tool.generic_error',
          severity: 'error',
          data: { title, message },
          summary: `error tile: ${title}${message ? ` — ${message}` : ''}`,
        },
        sourceTag: 'error',
      });
    },

    /**
     * Show a success message with optional buttons.
     *
     * 2026-05-10: structured event; renderer composes the reply. Buttons
     * are passed through as workflowActionButtonsHtml so they survive the
     * rendered message body.
     */
    showSuccess: (title: string, message: string, buttons?: ButtonConfig[]): void => {
      const templateButtons = buttons?.map((btn) => ({
        label: btn.label,
        id: btn.id,
        // Default rides the platform CTA token, not a theme accent (2026-08-22
        // owner directive: buttons line up with the special-element scheme).
        background: btn.background || PREST0_PRIMARY_CTA_BG,
        textColor: '#ffffff',
        dataAttributes: btn.dataAttributes,
        variant: 'primary' as const,
      }));
      const buttonsHtml = templateButtons && templateButtons.length > 0
        ? generateSpecialMessageTemplate({ header: { title: '', iconHtml: '' }, buttons: templateButtons, theme: 'green' })
        : undefined;

      helpers.addSystemMessage({
        payload: {
          kind: 'tool.generic_success',
          severity: 'success',
          data: { title, message, buttons: buttons || [] },
          summary: `success tile: ${title}${message ? ` — ${message}` : ''}`,
        },
        sourceTag: 'success',
        workflowActionButtonsHtml: buttonsHtml,
      });
    },

    /**
     * Show a warning message.
     *
     * 2026-05-10: structured event; renderer composes the reply.
     */
    showWarning: (title: string, message: string): void => {
      helpers.addSystemMessage({
        payload: {
          kind: 'tool.generic_warning',
          severity: 'warning',
          data: { title, message },
          summary: `warning tile: ${title}${message ? ` — ${message}` : ''}`,
        },
        sourceTag: 'warning',
      });
    },

    /**
     * Show an inline payment form in the chat.
     * The payment form will be rendered as an InlineInvoiceMessage component.
     */
    showPaymentForm: (config: {
      amountUsd: number;
      description: string;
      invoiceTitle: string;
      clientSecret: string;
      paymentIntentId: string;
      targetUid: string;
      allowedMethods: ('card' | 'bank' | 'ach')[];
      savedMethods: {
        cards: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number; isDefault: boolean }>;
        bankAccounts: Array<{ id: string; bankName: string; last4: string; accountType: string; isDefault: boolean }>;
      };
    }): void => {
      // Consume pending token usages (from orchestrator thinking/planning)
      const pendingUsages = sharedContext?.consumePendingTokenUsages?.() || [];

      // Create a message with payment messageType
      // The GlobalChatSystem will render this using InlineInvoiceMessage component
      // We store the payment config as JSON in a data attribute for the renderer to parse
      const paymentConfigJson = JSON.stringify({
        amountUsd: config.amountUsd,
        description: config.description,
        invoiceTitle: config.invoiceTitle,
        clientSecret: config.clientSecret,
        paymentIntentId: config.paymentIntentId,
        targetUid: config.targetUid,
        allowedMethods: config.allowedMethods,
        savedMethods: config.savedMethods,
        stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
      });

      // Generate a simple placeholder HTML that will be replaced by React component
      const html = `<div class="inline-payment-form" data-payment-config='${paymentConfigJson.replace(/'/g, "&#39;")}'></div>`;

      helpers.addSystemMessage({
        text: `Payment Required: $${config.amountUsd.toFixed(2)} - ${config.description || config.invoiceTitle}`,
        html,
        sourceTag: 'payment_form',
        messageType: 'payment' as any,
        // Attach pending token usages from orchestrator
        tokenUsages: pendingUsages.length > 0 ? pendingUsages : undefined,
        tokenUsage: pendingUsages.length === 1 ? pendingUsages[0] : undefined,
      });
    },

    /**
     * Show an inline appointment booking widget in the chat.
     * The appointment widget will be rendered as an InlineAppointmentMessage component.
     * Used to display Acuity scheduling iframe after payment success.
     */
    showAppointmentBooking: (config: {
      iframeUrl: string;
      title?: string;
      instructions?: string;
    }): void => {
      const appointmentConfigJson = JSON.stringify({
        iframeUrl: config.iframeUrl,
        title: config.title || 'Book Your Consultation',
        instructions: config.instructions || 'Please select a time that works best for you to schedule your consultation.',
      });

      // Generate placeholder HTML that will be replaced by React component
      const html = `<div class="inline-appointment-form" data-appointment-config='${appointmentConfigJson.replace(/'/g, "&#39;")}'></div>`;

      helpers.addSystemMessage({
        text: `Book Your Appointment: ${config.title || 'Consultation'}`,
        html,
        sourceTag: 'appointment_booking',
        messageType: 'appointment' as any,
      });
    },
  };
}

// ============================================================================
// Utility Functions for Tools
// ============================================================================

/**
 * Format research/document content to styled HTML.
 * Re-exports the existing helper for use by tools.
 */
export { formatResearchToHtml, formatDocumentToHtml, isDocumentContent };

/**
 * Create a simple HTML message with custom styling.
 */
export function createStyledHtml(
  content: string,
  options?: {
    color?: string;
    fontWeight?: string;
    fontSize?: string;
    fontStyle?: string;
  }
): string {
  const styles: string[] = [];
  if (options?.color) styles.push(`color:${options.color}`);
  if (options?.fontWeight) styles.push(`font-weight:${options.fontWeight}`);
  if (options?.fontSize) styles.push(`font-size:${options.fontSize}`);
  if (options?.fontStyle) styles.push(`font-style:${options.fontStyle}`);

  const styleAttr = styles.length > 0 ? ` style="${styles.join(';')}"` : '';
  return `<div${styleAttr}>${content}</div>`;
}

/**
 * Generate a button HTML string for use in custom templates.
 */
export function generateButtonHtml(button: ButtonConfig): string {
  const dataAttrs = button.dataAttributes
    ? Object.entries(button.dataAttributes)
        .map(([key, value]) => `data-${key}="${escapeHtml(value)}"`)
        .join(' ')
    : '';

  // Platform CTA token default (2026-08-22 owner directive) — was a hard blue.
  const bgColor = button.background || PREST0_PRIMARY_CTA_BG;
  const disabled = button.disabled ? 'disabled' : '';

  return `
    <button
      id="${escapeHtml(button.id)}"
      ${dataAttrs}
      ${disabled}
      style="
        padding: 8px 16px;
        background: ${bgColor};
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: ${button.disabled ? 'not-allowed' : 'pointer'};
        opacity: ${button.disabled ? '0.5' : '1'};
        transition: all 0.15s ease;
      "
    >
      ${escapeHtml(button.label)}
    </button>
  `.trim();
}

/**
 * Escape HTML special characters.
 */
function escapeHtml(text: string): string {
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
  };
  return text.replace(/[&<>"']/g, (char) => htmlEscapes[char] || char);
}

// ============================================================================
// Global Attachment Read Helpers
// ============================================================================

/**
 * Finalize a streaming message as an attachment read complete confirmation.
 *
 * This helper provides a standardized way for all workflows to confirm that
 * attachments have been read, with:
 * - Green-themed success message with checkmark icon
 * - File count and skipped files note
 * - Token usage for timing + info popup display
 * - Transaction recording for cost tracking
 *
 * @param stream - The streaming handle to finalize
 * @param result - The document analysis result from analyzeDocumentsWithClaude()
 * @param recordTransaction - The transaction recorder from StepContext
 * @param options - Optional customization
 */
export function finalizeAttachmentReadComplete(
  stream: StreamingHandle,
  result: DocumentAnalysisResult,
  recordTransaction: (params: {
    tokenUsage: TokenUsageMetadata;
    source: string;
    modelOverride?: string;
    summary?: string;
  }) => void,
  options?: {
    /** Source tag for the message (default: 'attachment_read_complete') */
    sourceTag?: string;
    /** Model display name for transaction (default: CLAUDE_DOCUMENT_ANALYSIS_DISPLAY) */
    modelDisplayName?: string;
    /** Transaction summary (default: 'document analysis') */
    transactionSummary?: string;
  }
): void {
  const {
    sourceTag = 'attachment_read_complete',
    modelDisplayName = CLAUDE_DOCUMENT_ANALYSIS_DISPLAY,
    transactionSummary = 'document analysis',
  } = options || {};

  const { filesAnalyzed, skippedFiles, tokenUsage } = result;

  // Build title with file count and optional skipped note
  const fileLabel = filesAnalyzed === 1 ? 'attachment' : 'attachments';
  const skippedNote = skippedFiles.length > 0
    ? ` (${skippedFiles.length} file${skippedFiles.length === 1 ? '' : 's'} skipped)`
    : '';
  const title = `${filesAnalyzed} ${fileLabel} read${skippedNote}`;

  // Generate styled HTML
  const html = generateSpecialMessageTemplate({
    header: { title, icon: '✓' },
    theme: 'green',
  });

  // Finalize with tokenUsage for timing/info display
  stream.finalize({
    text: title,
    html,
    sourceTag,
    tokenUsage,
  });

  // Record transaction for cost tracking. The display name derives from the
  // model that ACTUALLY served the analysis (fallback rungs included) —
  // previously the static default constant was recorded regardless of which
  // ladder model answered.
  if (tokenUsage) {
    recordTransaction({
      tokenUsage,
      source: 'agenticWorkflows',
      modelOverride: buildModelDisplay(tokenUsage, '', modelDisplayName),
      summary: transactionSummary,
    });
  }
}

// ============================================================================
// Enhanced Message Template System
// ============================================================================

/**
 * Card configuration for createCardLayout
 */
export interface CardConfig {
  /** Card title (optional) */
  title?: string;
  /** Card content HTML */
  content: string;
  /** Card type affects visual styling */
  type?: 'default' | 'success' | 'warning' | 'info' | 'highlight';
  /** Whether card can be collapsed */
  collapsible?: boolean;
  /** Whether card starts expanded (only if collapsible) */
  defaultExpanded?: boolean;
  /** Preview text shown when collapsed */
  preview?: string;
}

/**
 * Generate a card-based message layout for better visual hierarchy.
 * Cards create clear visual separation and add depth with shadows.
 */
export function createCardLayout(options: {
  title: string;
  subtitle?: string;
  icon?: string;
  cards: CardConfig[];
  buttons?: ButtonConfig[];
  theme?: 'blue' | 'green' | 'purple' | 'amber' | 'red';
}): string {
  const theme = options.theme || 'blue';
  const colors = resolveThemeColors(theme);

  // Main container with modern styling
  const containerStyle = `
    font-family: -apple-system, 'SF Pro Display', 'SF Pro Text', system-ui, sans-serif;
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #E5E7EB;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  `.trim();

  // Header section
  const iconHtml = options.icon
    ? `<div style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:${colors.accentBg};color:${colors.accent};border-radius:50%;font-size:16px;margin-right:12px;">${options.icon}</div>`
    : '';

  const headerHtml = `
    <div style="display:flex;align-items:center;margin-bottom:${options.subtitle ? '8px' : '20px'};">
      ${iconHtml}
      <h1 style="margin:0;font-size:18px;font-weight:800;color:#111827;">${escapeHtml(options.title)}</h1>
    </div>
    ${options.subtitle ? `<p style="margin:0 0 20px 0;font-size:14px;color:#6B7280;line-height:1.5;">${escapeHtml(options.subtitle)}</p>` : ''}
  `.trim();

  // Card styles by type
  const getCardAccent = (type: CardConfig['type']): string => {
    switch (type) {
      case 'success': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'info': return '#3B82F6';
      case 'highlight': return '#FDE68A';
      default: return colors.accent;
    }
  };

  const getCardBackground = (type: CardConfig['type']): string => {
    switch (type) {
      case 'highlight': return '#FFFBEB';
      default: return '#ffffff';
    }
  };

  // Generate cards
  const cardsHtml = options.cards.map((card, idx) => {
    const cardId = `card-${idx}-${Date.now()}`;
    const accentColor = getCardAccent(card.type);
    const bgColor = getCardBackground(card.type);
    const isCollapsible = card.collapsible || false;
    const isExpanded = card.defaultExpanded !== false; // Default to expanded

    const cardStyle = `
      background: ${bgColor};
      border: 1px solid #E5E7EB;
      border-left: 4px solid ${accentColor};
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
      transition: all 0.2s ease;
    `.trim();

    const titleHtml = card.title
      ? `<div style="font-size:14px;font-weight:600;color:#374151;margin-bottom:${isCollapsible && !isExpanded ? '4px' : '12px'};${isCollapsible ? 'cursor:pointer;user-select:none;' : ''}" ${isCollapsible ? `onclick="toggleCard('${cardId}')"` : ''}>${card.title}${isCollapsible ? ' <span id="${cardId}-chevron" style="font-size:12px;transition:transform 0.3s ease;">' + (isExpanded ? '▼' : '▶') + '</span>' : ''}</div>`
      : '';

    const previewHtml = isCollapsible && !isExpanded && card.preview
      ? `<div id="${cardId}-preview" style="font-size:13px;color:#6B7280;font-style:italic;line-height:1.4;max-height:2.8em;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;">${escapeHtml(card.preview)}</div>`
      : '';

    const contentStyle = `
      font-size: 13px;
      color: #111827;
      line-height: 1.6;
      ${isCollapsible && !isExpanded ? 'display:none;' : ''}
    `.trim();

    return `
      <div style="${cardStyle}">
        ${titleHtml}
        ${previewHtml}
        <div id="${cardId}-content" style="${contentStyle}">
          ${card.content}
        </div>
      </div>
    `.trim();
  }).join('\n');

  // Buttons section
  const buttonsHtml = options.buttons && options.buttons.length > 0
    ? `
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:16px;padding-top:16px;border-top:1px solid #E5E7EB;">
        ${options.buttons.map(btn => generateButtonHtml(btn)).join('')}
      </div>
    `.trim()
    : '';

  return `
    <div style="${containerStyle}">
      ${headerHtml}
      ${cardsHtml}
      ${buttonsHtml}
    </div>
  `.trim();
}

/**
 * Create status badges for message metadata.
 */
export function createStatusBadge(options: {
  label: string;
  type: 'new' | 'updated' | 'draft' | 'complete' | 'pending';
  size?: 'small' | 'medium';
}): string {
  const size = options.size || 'medium';
  const height = size === 'small' ? '18px' : '24px';
  const fontSize = size === 'small' ? '0.6rem' : '0.65rem';
  const padding = size === 'small' ? '2px 8px' : '4px 10px';

  const colors: Record<typeof options.type, { bg: string; text: string }> = {
    new: { bg: '#3B82F6', text: '#ffffff' },
    updated: { bg: '#8B5CF6', text: '#ffffff' },
    draft: { bg: '#F59E0B', text: '#ffffff' },
    complete: { bg: '#10B981', text: '#ffffff' },
    pending: { bg: '#6B7280', text: '#ffffff' },
  };

  const color = colors[options.type];

  return `
    <span style="
      display:inline-block;
      background:${color.bg};
      color:${color.text};
      font-size:${fontSize};
      font-weight:600;
      text-transform:lowercase;
      letter-spacing:0.05em;
      padding:${padding};
      border-radius:12px;
      height:${height};
      line-height:${height};
      vertical-align:middle;
      margin-right:8px;
    ">${escapeHtml(options.label)}</span>
  `.trim();
}

/**
 * Visual progress bar for multi-step workflows.
 */
export function createProgressBar(options: {
  currentStep: number;
  totalSteps: number;
  stepLabels?: string[];
  theme?: 'blue' | 'green' | 'purple';
}): string {
  const theme = options.theme || 'blue';
  const colors = resolveThemeColors(theme);
  const percentage = (options.currentStep / options.totalSteps) * 100;

  const barHtml = `
    <div style="margin-bottom:${options.stepLabels ? '12px' : '0'};">
      <div style="
        width:100%;
        height:8px;
        background:#E5E7EB;
        border-radius:4px;
        overflow:hidden;
        position:relative;
      ">
        <div style="
          width:${percentage}%;
          height:100%;
          background:linear-gradient(90deg, ${colors.accent}, ${colors.accentBg});
          transition:width 0.3s ease;
          border-radius:4px;
        "></div>
      </div>
    </div>
  `.trim();

  const labelsHtml = options.stepLabels
    ? `
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
        ${options.stepLabels.map((label, idx) => {
          const stepNum = idx + 1;
          const isComplete = stepNum < options.currentStep;
          const isCurrent = stepNum === options.currentStep;
          const isPending = stepNum > options.currentStep;

          const color = isComplete ? '#10B981' : isCurrent ? colors.accent : '#9CA3AF';
          const icon = isComplete ? '✓' : stepNum;

          return `
            <div style="display:flex;flex-direction:column;align-items:center;flex:1;text-align:center;">
              <div style="
                width:24px;
                height:24px;
                border-radius:50%;
                background:${color};
                color:#ffffff;
                display:flex;
                align-items:center;
                justify-content:center;
                font-size:11px;
                font-weight:600;
                margin-bottom:4px;
                ${isCurrent ? 'animation:pulse 2s cubic-bezier(0.4,0,0.6,1) infinite;' : ''}
              ">${icon}</div>
              <div style="font-size:11px;color:${color};font-weight:${isCurrent ? '600' : '500'};">${escapeHtml(label)}</div>
            </div>
          `.trim();
        }).join('')}
      </div>
      <style>
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      </style>
    `.trim()
    : '';

  return `
    <div style="margin-bottom:16px;">
      ${barHtml}
      ${labelsHtml}
    </div>
  `.trim();
}

/**
 * Visual dividers between major sections.
 */
export function createSectionDivider(options: {
  label?: string;
  icon?: string;
  style?: 'line' | 'gradient' | 'dashed';
}): string {
  const dividerStyle = options.style || 'line';

  const lineStyles: Record<typeof dividerStyle, string> = {
    line: 'border-top:1px solid #E5E7EB;',
    gradient: 'border-top:1px solid transparent;background:linear-gradient(90deg, transparent, #E5E7EB 50%, transparent);height:1px;',
    dashed: 'border-top:2px dashed #E5E7EB;',
  };

  if (options.label || options.icon) {
    return `
      <div style="display:flex;align-items:center;gap:12px;margin:24px 0;">
        <div style="flex:1;${lineStyles[dividerStyle]}"></div>
        <div style="font-size:13px;font-weight:600;color:#6B7280;white-space:nowrap;">
          ${options.icon ? `<span style="margin-right:6px;">${options.icon}</span>` : ''}
          ${options.label ? escapeHtml(options.label) : ''}
        </div>
        <div style="flex:1;${lineStyles[dividerStyle]}"></div>
      </div>
    `.trim();
  }

  return `<div style="margin:24px 0;${lineStyles[dividerStyle]}"></div>`;
}
